import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { UserRepository } from '@domain/repositories/user.repository';
import { PaginatedData } from '@base/wrappers/paginated-data';
import {
  UserModel,
  UserPlansSummaryFilters,
  UserPlansSummaryModel,
} from '@domain/models/user.model';
import { UserPlansSummaryMapper } from './mappers/user-plans-summary.mapper';
import { environment } from '@env/environment';
import { ResponseData } from '@base/wrappers/response-data';
import { UserEntity, UserPlansSummaryEntity } from './entities/user-entity';
import { UserMapper } from './mappers/user.mapper';
import { UpdateUserBatchModel, UpdateUserBatchResult } from '@domain/models/update-user-batch.model';
import { GetUsersPlanSummaryData } from '@domain/usecases/user/get-users-plans-summary.usecase';

@Injectable({
  providedIn: 'root',
})
export class UserImplRepository extends UserRepository {

  private readonly userUrl: string;
  private readonly userMapper = new UserMapper();
  private readonly userPlansSummaryMapper = new UserPlansSummaryMapper();

  constructor(private readonly http: HttpClient) {
    super();
    this.userUrl = `${environment.uriBase}User`;
  }

  override getUser(idUser: string): Observable<UserModel> {
    return this.http
      .get<ResponseData<UserEntity>>(`${this.userUrl}/${idUser}`)
      .pipe(
        map((result) => {
          return this.userMapper.mapFrom(result.data);
        })
      );
  }

  override getUsersPlansSummary(
    filters: GetUsersPlanSummaryData
  ): Observable<PaginatedData<UserPlansSummaryModel>> {
    let params = new HttpParams();

    Object.keys(filters).forEach((key) => {
      let value = filters[key as keyof GetUsersPlanSummaryData];

      if (value !== null && value !== undefined) {
        if (key === 'sortBy') {
          value = this.capitalize(value.toString());
        }

        params = params.append(key, value.toString());
      }
    });

    params = params.append('Role', 'Customer');

    return this.http
      .get<ResponseData<PaginatedData<UserPlansSummaryEntity>>>(
        this.userUrl,
        { params }
      )
      .pipe(
        map((result) => ({
          ...result.data,
          data: (result.data.data ?? []).map(
            this.userPlansSummaryMapper.mapFrom
          ),
        }))
      );
  }
  override postChangePassword(
    idUser: string,
    currentPassword: string,
    newPassword: string
  ): Observable<boolean> {
    return this.http
      .post<ResponseData<boolean>>(
        `${this.userUrl}/${idUser}/change-password`,
        {
          currentPassword: currentPassword,
          newPassword: newPassword,
        }
      )
      .pipe(
        map((result) => {
          return true;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.error.message === 'Current password is invalid') {
            return throwError(() => 'La contraseña actual no es válida');
          }
          return throwError(() => error.error.message);
        })
      );
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  override updateUserBatch(updateBatchData: UpdateUserBatchModel[]): Observable<UpdateUserBatchResult[]> {
    return this.http.post<ResponseData<UpdateUserBatchResult[]>>(`${this.userUrl}/update-batch`, {updateInfo: updateBatchData})
    .pipe(map((response) => response.data))
  }

  override getUserExcelReport(filters: UserPlansSummaryFilters): Observable<File> {
     let params = new HttpParams();

    Object.keys(filters).forEach((key) => {
      let value = filters[key as keyof UserPlansSummaryFilters];

      if (value !== null && value !== undefined) {
        if (key === 'sortBy') {
          value = this.capitalize(value.toString());
        }

        params = params.append(key, value.toString());
      }
    });

    params = params.append('Role', 'Customer'); 

    return this.http
      .get<Blob>(
        `${this.userUrl}/excel-report`,
        { params, responseType: 'blob' as 'json', observe: 'response' }
      )
      .pipe(
        map((response) => {
          const binaryData = [response.body!]
          return new File(binaryData, 'tmp');
        })
      );
  }

  override deleteUserBatch(ids: string[]): Observable<void> {
    return this.http.delete<void>(`${this.userUrl}/delete-massive`, {body: { ids }})
  }
}
